import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
} from "@mui/material";

const money2 = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const disabledFieldSx = {
  "& .Mui-disabled": {
    backgroundColor: "#f0f0f0 !important",
    color: "#333 !important",
    WebkitTextFillColor: "#333 !important",
  },
};

const cleanLabel = (value) => {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const normalizeTotales = (doc) => {
  const t = doc?.totales && typeof doc.totales === "object" ? doc.totales : {};

  return {
    subtotal_15: money2(t.subtotal_15 ?? 0),
    subtotal_5: money2(t.subtotal_5 ?? 0),
    subtotal_0: money2(t.subtotal_0 ?? 0),
    subtotal_no_objeto: money2(t.subtotal_no_objeto ?? t.subtotal_no_obj ?? 0),
    descuento_total: money2(t.descuento_total ?? t.descuento ?? 0),
    iva_15: money2(t.iva_15 ?? 0),
    iva_5: money2(t.iva_5 ?? 0),
    ice: money2(t.ice ?? 0),
    total: money2(t.total ?? doc?.total ?? 0),
  };
};

export default function InvoiceAccount({ doc }) {
  const rows = Array.isArray(doc?.gastos) ? doc.gastos : [];
  const tot = useMemo(() => normalizeTotales(doc), [doc]);

  const descripcionDoc = String(
    doc?.descripcion ||
      doc?.descripcion_factura ||
      doc?.referencia ||
      ""
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mt: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Cuenta</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Tipo</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Base</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA</TableCell>
              <TableCell sx={{ color: "#fff" }}>Total</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 ? (
              rows.map((r, i) => (
                <TableRow key={r.id || i}>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography sx={{ color: "#111827", fontWeight: 700 }}>
                      {`${r.cuenta_codigo || ""} ${r.cuenta_nombre || ""}`.trim() || "Sin cuenta"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 260 }}>
                    <Typography sx={{ color: "#111827" }}>
                      {r.descripcion || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={cleanLabel(r.cuenta_tipo)}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 180, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={Number(r.iva_porcentaje || 0) === 999 ? "No objeto" : String(r.iva_porcentaje || 0)}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={`$${money2(r.descuento_valor)}`}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={`$${money2(r.base_imponible)}`}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={`$${money2(r.iva_valor)}`}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={`$${money2(r.total)}`}
                      disabled
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ width: 90, ...disabledFieldSx }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography sx={{ fontWeight: 700, color: "#555" }}>
                    No hay cuentas registradas en este documento.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography sx={{ mb: 1 }}>
            Descripción: <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={descripcionDoc}
            multiline
            minRows={3}
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
            sx={disabledFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Grid container spacing={1}>
              {[
                ["Subtotal 15%", tot.subtotal_15],
                ["Subtotal 5%", tot.subtotal_5],
                ["Subtotal 0%", tot.subtotal_0],
                ["Subtotal No Objeto", tot.subtotal_no_objeto],
                ["Descuento", tot.descuento_total],
                ["IVA 15%", tot.iva_15],
                ["IVA 5%", tot.iva_5],
                ["ICE", tot.ice],
                ["Total", tot.total],
              ].map(([label, value], index) => (
                <React.Fragment key={index}>
                  <Grid item xs={7}>
                    <Typography
                      sx={{
                        color: "#333",
                        fontWeight: label === "Total" ? "bold" : "normal",
                      }}
                    >
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      value={`$${money2(value)}`}
                      fullWidth
                      size="small"
                      disabled
                      sx={disabledFieldSx}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}